import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { isAfter, isBefore, parseISO } from 'date-fns';
import Swal from 'sweetalert2/dist/sweetalert2.all.js';
import { Orders } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { SearchService } from 'src/app/services/search.service';
import { SearchServiceService } from 'src/app/services/shared/search-service.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * Ordini PM — admin list of "custom-made" / "made-to-measure" orders
 * (called "CM" in the backend, "PM" in the legacy UI; the route is
 * `/pm-orders`). These orders are functionally identical to regular
 * orders once they enter the prep flow — what makes them special is
 * the **scadenza timer**: each PM order has an `or_days_remaining`
 * countdown from the order date, and when the timer expires we show
 * a daily Swal notice "Non hai preparato l'ordine entro il tempo
 * indicato". The progress bar in the template walks the 24-month
 * deadline window.
 *
 * Phase 1.5 / v2.6.0 — TS cleaned up:
 *   - dropped ~25 `console.log` debug lines,
 *   - dropped `FormBuilder` + `assignOrderForm` + `assignOrder()`
 *     (declared but only called `markAllAsTouched()` — pure dead
 *     code, no POST),
 *   - dropped `orderNew` / `orderToSend` / `priorityOptions` /
 *     `carriers` / `workers` / `productsNew` / `maxPriority` /
 *     `refreshInterval` / `timer` / `text` / `filteredOrder` (all
 *     unused),
 *   - dropped `dateBy` (used only to seed a static label, now
 *     hardcoded),
 *   - dropped manual `Subscription` accumulation in favour of
 *     `takeUntilDestroyed`,
 *   - added `startDateStr` / `endDateStr` bridge getters so the
 *     template can use native `<input type="date">` instead of
 *     `<kendo-datepicker>`,
 *   - kept the deadline / progress business logic intact (the only
 *     thing that justifies this page existing as a separate route).
 */
@Component({
  selector: 'app-ordini-pm',
  templateUrl: './ordini-pm.component.html',
  styleUrls: ['./ordini-pm.component.scss'],
  standalone: false,
})


export class OrdiniPMComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly route = inject(ActivatedRoute);
  private readonly openModal = inject(OpenModalService);
  private readonly filterService = inject(SearchService);
  private readonly searchService = inject(SearchServiceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // Make services available to the template where needed.
  public readonly openModalRef = this.openModal;

  // ── Data ──────────────────────────────────────────────────────────
  public gridData: Orders.ICMOrderList[] = [];
  public loading = false;

  // ── Search ────────────────────────────────────────────────────────
  public filterTerm = '';
  public selectedCustomer: string | null = null;
  public booleanForSpecialSearch = false;
  public clickedOrder: number = -1;

  // ── Date range filter ────────────────────────────────────────────
  public startDate: Date | null = new Date(2021, 0, 1);
  public endDate: Date | null = new Date();
  public filters = {
    dateOrginizedBy: { text: 'Data ordine', value: 'or_order_number' as const },
    startingDate: this.startDate,
    endDate: this.endDate,
    minDate: new Date(),
    maxDate: new Date(),
  };

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // Bridge getters for `<input type="date">` (replaces kendo-datepicker).
  get startDateStr(): string { return this.toInputDate(this.startDate); }
  set startDateStr(value: string) { this.startDate = this.fromInputDate(value); this.filters.startingDate = this.startDate; }
  get endDateStr(): string { return this.toInputDate(this.endDate); }
  set endDateStr(value: string) { this.endDate = this.fromInputDate(value); this.filters.endDate = this.endDate; }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    // Daily expiry-reminder Swal — fires once per day per browser.
    if (!this.isNotificationSentToday()) {
      this.sendNotification();
      this.markNotificationAsSent();
    }
    this.filterTerm = this.filterService.getFilterText() ?? '';
    if (this.filterTerm) {
      this.searchOrders(this.filterTerm);
    } 
    else {
      this.reloadFunction();
    }
  }

  ngOnDestroy(): void {
    if (!this.booleanForSpecialSearch) {
      localStorage.removeItem('query-search');
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Search + reload.
  // ──────────────────────────────────────────────────────────────────

  handleFilterChange(): void {
    this.filterService.setFilterText(this.filterTerm);
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = new Date(2021, 0, 1);
    this.startDate = this.filters.startingDate;
    this.filterService.clearFilterText();
    this.reloadFunction();
  }

  onInputChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.validity && inputElement.validity.valueMissing) {
      this.getCMOrders();
    }
  }

  reloadFunction(): void {
    this.loading = true;
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.getCMOrders();
        } 
        else {
          const searchParam = params['search'] as string | undefined;
          const dateParam = params['data'] as string | undefined;
          if (dateParam) {
            const parsed = parseISO(dateParam);
            this.filters.startingDate = parsed;
            this.startDate = parsed;
          }
          this.searchOrders(searchParam);
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  getCMOrders(): void {
    this.loading = true;
    this.ordersServ
      .getCMOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Orders.ICMOrderList[]) => {
          this.gridData = Array.isArray(data) ? data : [];
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  searchOrders(searchString?: string): void {
    if (searchString !== undefined) this.filterTerm = searchString ?? '';
    if (!this.filterTerm) {
      this.getCMOrders();
      return;
    }
    this.loading = true;
    this.ordersServ
      .searchCmOrder(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Orders.ICMOrderList[]) => {
          if (Array.isArray(data)) {
            this.gridData = data;
            localStorage.setItem('query-search', this.filterTerm);
            this.ordersServ.passFilterValue(this.filterTerm);
            this.updateDateBracketsFromData();
          } 
          else {
            this.gridData = [];
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.gridData = [];
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Row actions.
  // ──────────────────────────────────────────────────────────────────

  assignPM(orderGammaId: number): void {
    this.clickedOrder = orderGammaId;
    this.openModal.onStorico();
  }

  onChildClose(result: any): void {
    if (result) this.reloadFunction();
  }

  // ──────────────────────────────────────────────────────────────────
  // Deadline / progress business logic (preserved from legacy).
  // ──────────────────────────────────────────────────────────────────

  /** Deadline date = order_date + |or_days_remaining| × 1 day. */
  deadline(item: Orders.ICMOrderList): Date {
    const orderDate = new Date(item.or_order_date);
    const daysRemaining = (item as any).or_days_remaining ?? 0;
    return new Date(orderDate.getTime() + Math.abs(daysRemaining) * 86_400_000);
  }

  isExpired(orderDate: string | Date): boolean {
    const lastDate = this.expirationDateFromOrderDate(orderDate);
    return new Date() > lastDate;
  }

  /**
   * Progress along the 24-month deadline window, in percent. Kept
   * monthly-bucketed instead of daily to keep the visualisation
   * readable across long deadlines.
   */
  progress(orderDate: string | Date): number {
    const finalDate = this.expirationDateFromOrderDate(orderDate);
    const start = new Date(orderDate);
    const months =
      (finalDate.getFullYear() - start.getFullYear()) * 12 +
      (finalDate.getMonth() - start.getMonth());
    const progresso = (months / 24) * 100;
    return Math.max(0, Math.min(100, progresso));
  }

  isMonthComplete(daysRemaining: number): boolean {
    // Preserved business logic: 24-month window with one-day grace.
    return daysRemaining >= 0 || daysRemaining > -24 * 30;
  }

  daysRemaining(item: Orders.ICMOrderList): number {
    const deadlineDate = this.deadline(item);
    const remainingDays = Math.ceil(
      (deadlineDate.getTime() - Date.now()) / 86_400_000,
    );
    return remainingDays > 0 ? remainingDays : 0;
  }

  /**
   * 5-bucket status for the SCSS to colour the progress bar:
   *   - expired (red): deadline passed
   *   - urgent  (orange): ≤ 7 days left
   *   - warning (amber): ≤ 30 days left
   *   - normal  (blue): more time
   *   - ok      (green): brand new (≤ 25 % consumed)
   */
  deadlineState(item: Orders.ICMOrderList): 'expired' | 'urgent' | 'warning' | 'normal' | 'ok' {
    if (this.isExpired(item.or_order_date)) return 'expired';
    const days = this.daysRemaining(item);
    if (days <= 7) return 'urgent';
    if (days <= 30) return 'warning';
    if (this.progress(item.or_order_date) < 25) return 'ok';
    return 'normal';
  }

  // ──────────────────────────────────────────────────────────────────
  // Daily expiry notification (preserved).
  // ──────────────────────────────────────────────────────────────────

  isNotificationSentToday(): boolean {
    const last = localStorage.getItem('lastNotificationDate');
    if (!last) return false;
    return new Date().toDateString() === new Date(last).toDateString();
  }

  markNotificationAsSent(): void {
    localStorage.setItem('lastNotificationDate', new Date().toISOString());
  }

  sendNotification(): void {
    Swal.fire({
      icon: 'warning',
      title: 'Notifica giornaliera',
      text: "La data di scadenza dell'ordine è imminente!!",
      timer: 1500,
      timerProgressBar: true,
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Helper consumed by `isExpired` / `progress`. The legacy
   * `expirationDate(date)` lived as an instance method but
   * effectively computed `orderDate + 24 months`. Re-implemented as
   * a single private helper to remove the call-chain.
   */
  private expirationDateFromOrderDate(orderDate: string | Date): Date {
    const d = new Date(orderDate);
    return new Date(d.getFullYear() + 2, d.getMonth(), d.getDate());
  }

  /**
   * After a search, widen the visible date-range so the user sees
   * the relevant orders even if their date filter was set to a
   * narrower window. Behaviour preserved from the legacy code.
   */
  private updateDateBracketsFromData(): void {
    for (const element of this.gridData) {
      const dt = parseISO(element.or_order_date);
      if (isBefore(dt, this.filters.minDate)) {
        this.filters.startingDate = dt;
        this.filters.minDate = dt;
        this.startDate = dt;
      }
      if (isAfter(dt, this.filters.maxDate)) {
        this.filters.startingDate = dt;
        this.filters.maxDate = dt;
        this.startDate = dt;
      }
    }
  }

  private toInputDate(d: Date | null): string {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  private fromInputDate(v: string): Date | null {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  get filteredOrders(): Orders.ICMOrderList[] {
    const customer = this.selectedCustomer;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.gridData.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if (start && o.or_order_date && new Date(o.or_order_date) < start) {
        return false;
      }
      if (end && o.or_order_date && new Date(o.or_order_date) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
